/**
 * General PowerPoint add-in handlers (non-Deckorate).
 *
 * Keyed by the `frontend_binding.handler` of each ToolRegistry entry with
 * `hosts: ["powerpoint"]`, and by the `handler` of each action-bar pill.
 * `getHandlersForHost("powerpoint")` merges these with Deckorate's: the two
 * never collide because Deckorate's keys are all `deckorate.*`.
 *
 * Two kinds, mirroring the Word folder:
 *  - `powerpoint.*` — the Office.js write a pill click runs.
 *  - `ui.pill.ppt.*` — the answer to a model that calls a pill tool itself.
 *    These render nothing (AIVA-8972): the add-in builds the action bar at
 *    turn end (src/chat/pills/powerpointPills.ts), and that bar is the only
 *    one shown.
 */

import { buildSlideShapesToolResult } from "../../../src/integrations/powerpoint/deckIntelligence/slideContent";
import { buildSpeakerNotesToolResult } from "../../../src/integrations/powerpoint/features/list-speaker-notes/services/buildSpeakerNotesToolResult";
import { readAllSpeakerNotes } from "../../../src/integrations/powerpoint/features/list-speaker-notes/services/readAllSpeakerNotes";
import { buildDeckContext } from "../../../src/integrations/powerpoint/features/summarise-deck/services/buildDeckContext";
import { readDeckSlides } from "../../../src/integrations/powerpoint/features/summarise-deck/services/readDeckSlides";
import { addAsNewSlide } from "../../../src/integrations/powerpoint/services/addAsNewSlide";
import { addSpeakerNotes } from "../../../src/integrations/powerpoint/services/addSpeakerNotes";
import { copyResponseText } from "../../../src/integrations/powerpoint/services/copyResponse";
import { insertTextIntoCurrentSlide } from "../../../src/integrations/powerpoint/services/insertIntoSlide";
import { replaceSelectedShapeText } from "../../../src/integrations/powerpoint/services/replaceSelection";
import { readCurrentSlideShapes } from "../../../src/integrations/powerpoint/services/slideShapes";
import {
  PPT_ADD_AS_NEW_SLIDE,
  PPT_ADD_SPEAKER_NOTES,
  PPT_COPY,
  PPT_INSERT_INTO_SLIDE,
  PPT_REPLACE_SELECTION,
  type PowerPointPillSpec,
} from "../../../src/chat/pills/powerpointPills";

export type HandlerFn = (args: Record<string, unknown>) => Promise<unknown>;

function textArg(args: Record<string, unknown>, toolId: string): string {
  const text = typeof args.text === "string" ? args.text.trim() : "";
  if (!text) throw new Error(`${toolId} requires non-empty text`);
  return text;
}

/**
 * A model that calls a `pill_ppt_*` tool itself (AIVA-8972). No pill is
 * rendered: the bar the add-in builds at turn end (`buildPowerPointPills`)
 * places the reply, and only after checking the reply is slide content. A
 * model pill skipped that check and carried whatever text the model chose —
 * for a request with no brief, the user's own prompt, which Insert into slide
 * then put on the slide under a reply asking for the brief.
 *
 * Still resolves `rendered: true` so the suspended turn resumes, and still
 * rejects a call with no text. A reply the model left empty is filled from
 * this text before the bar is built (turnEvents `applyEmptyBubbleFallback`).
 * Nothing here awaits, so it is not `async`; the promise chain keeps a missing
 * `text` a rejection, exactly as an async function would report it.
 */
function acknowledgePptPill(spec: PowerPointPillSpec, args: Record<string, unknown>): Promise<{ rendered: true }> {
  return Promise.resolve().then(() => {
    textArg(args, spec.toolId);
    return { rendered: true as const };
  });
}

/**
 * AIVA-6811 — `ppt_get_deck_text`: every slide's text, cut like the DI-01 summary.
 * A failed read is data (readOk false), not a throw, so the model can tell it from an empty deck.
 */
async function getDeckText(): Promise<Record<string, unknown>> {
  const slides = await readDeckSlides();
  if (slides === null) return { readOk: false, message: "The presentation could not be read in this version of PowerPoint." };
  const read = buildDeckContext(slides);
  if (read.kind !== "ok") {
    const empty = { text: "", slidesIncluded: 0, truncated: false, message: "No slide in the presentation has any text." };
    return { readOk: true, totalSlides: slides.length, ...empty };
  }
  const { text, slidesIncluded, totalSlides, truncated } = read.context;
  return { readOk: true, text, slidesIncluded, totalSlides, truncated };
}

// Keys are string literals, not `[PPT_INSERT_INTO_SLIDE.handler]`, so the
// capability drift guard (tests/chat/capabilityMap.test.ts) can read them from
// this file; the handlers test pins each key to its PPT_PILL_SPECS row.
export const handlers: Record<string, HandlerFn> = {
  // SC-02 — add the reply as a new text box on the selected slide.
  "powerpoint.insert_text": async (args) =>
    await insertTextIntoCurrentSlide(textArg(args, PPT_INSERT_INTO_SLIDE.handler)),

  // SC-02 — a model call to `pill_ppt_insert_into_slide`.
  "ui.pill.ppt.insert_into_slide": (args) => acknowledgePptPill(PPT_INSERT_INTO_SLIDE, args),

  // SC-03 — replace the selected shape's text with the reply.
  "powerpoint.replace_selection": async (args) =>
    await replaceSelectedShapeText(textArg(args, PPT_REPLACE_SELECTION.handler)),

  // SC-03 — a model call to `pill_ppt_replace_selection`.
  "ui.pill.ppt.replace_selection": (args) => acknowledgePptPill(PPT_REPLACE_SELECTION, args),

  // SC-04 — append the reply to the current slide's speaker notes.
  "powerpoint.add_speaker_notes": async (args) =>
    await addSpeakerNotes(textArg(args, PPT_ADD_SPEAKER_NOTES.handler)),

  // SC-04 — a model call to `pill_ppt_add_speaker_notes`.
  "ui.pill.ppt.add_speaker_notes": (args) => acknowledgePptPill(PPT_ADD_SPEAKER_NOTES, args),

  // SC-05 — add the reply as a new slide after the selected one.
  "powerpoint.add_as_new_slide": async (args) =>
    await addAsNewSlide(textArg(args, PPT_ADD_AS_NEW_SLIDE.handler)),

  // SC-05 — a model call to `pill_ppt_add_as_new_slide`.
  "ui.pill.ppt.add_as_new_slide": (args) => acknowledgePptPill(PPT_ADD_AS_NEW_SLIDE, args),

  // ST-08 — copy the raw reply text to the clipboard.
  "powerpoint.copy_text": async (args) => await copyResponseText(textArg(args, PPT_COPY.handler)),

  // ST-08 — a model call to `pill_ppt_copy_text`.
  "ui.pill.ppt.copy_text": (args) => acknowledgePptPill(PPT_COPY, args),

  // AIVA-6811 — read the open deck for `ppt_general` (tool `ppt_get_deck_text`).
  "powerpoint.get_deck_text": getDeckText,

  // AIVA-8913 — DI-02 for `ppt_general` when the notes regex missed (tool
  // `ppt_get_speaker_notes`). A failed read is data (readOk false), never a throw.
  "powerpoint.get_speaker_notes": async () => buildSpeakerNotesToolResult(await readAllSpeakerNotes()),

  // AIVA-8913 — DI-04 for `ppt_general` when the slide-content regex missed
  // (tool `ppt_get_slide_shapes`). The reader never throws.
  "powerpoint.get_slide_shapes": async () => buildSlideShapesToolResult(await readCurrentSlideShapes()),
};
